// Small helper to centralize the per-age-year computation tied to economic life
import type { DepreciationSettingsV2, Improvements } from '@aloft/toolkit-shared';

import type { CostNewByCategory } from '../../../redux/features/selectors/costSelectors/selectCostNewByCategory.ts';

export function computeImprovementValuePerAgeYear(
  improvementValue: number,
  effectiveAge: number,
  economicLife: number,
): number {
  const safeImprovement = Number.isFinite(improvementValue) ? improvementValue : 0;
  const safeLife = Number.isFinite(economicLife) && economicLife > 0 ? economicLife : 60;
  // If effective age is invalid, assume mid-life as a neutral default
  const safeAge = Number.isFinite(effectiveAge) ? effectiveAge : safeLife / 2;

  const remainingYears = Math.max(safeLife - safeAge, 0);
  return remainingYears <= 0 ? 0 : safeImprovement / remainingYears;
}

export function computeImprovementValuePerAgeYearForProperty(args: {
  depreciateSeparately: boolean;
  improvementValue: number;
  effectiveAge: number;
  defaultEconomicLife: number;
  byCategory?: CostNewByCategory;
  improvementsEco?: Partial<Improvements>;
  depreciationDefaults?: DepreciationSettingsV2;
}): number {
  const {
    depreciateSeparately,
    improvementValue,
    effectiveAge,
    defaultEconomicLife,
    byCategory,
    improvementsEco,
    depreciationDefaults,
  } = args;

  if (!depreciateSeparately) {
    return computeImprovementValuePerAgeYear(improvementValue, effectiveAge, defaultEconomicLife);
  }

  if (!byCategory) return 0;

  const defaultMainEco = Number.isFinite(defaultEconomicLife) ? defaultEconomicLife : 60;
  const ecoFor = (category: string): number => {
    const imp: Partial<Improvements> = improvementsEco ?? {};
    const defs: Partial<DepreciationSettingsV2> = depreciationDefaults ?? {};
    switch (category) {
      case 'garage':
        return imp.garageEcoLife ?? defs.defaultEcoLifeGarage ?? defaultMainEco;
      case 'detachedGarage':
        return imp.detachedGarageEcoLife ?? defs.defaultEcoLifeDetachedGarage ?? defaultMainEco;
      case 'carport':
        return imp.carportEcoLife ?? defs.defaultEcoLifeCarport ?? defaultMainEco;
      case 'porch':
        return imp.porchEcoLife ?? defs.defaultEcoLifePorch ?? defaultMainEco;
      case 'patio':
        return imp.patioEcoLife ?? defs.defaultEcoLifePatio ?? defaultMainEco;
      case 'deck':
        return imp.deckEcoLife ?? defs.defaultEcoLifeDeck ?? defaultMainEco;
      case 'workshop':
        return imp.workshopEcoLife ?? defs.defaultEcoLifeWorkshop ?? defaultMainEco;
      case 'shed':
        return imp.shedEcoLife ?? defs.defaultEcoLifeShed ?? defaultMainEco;
      case 'barn':
        return imp.barnEcoLife ?? defs.defaultEcoLifeBarn ?? defaultMainEco;
      case 'pool':
        return imp.poolEcoLife ?? defs.defaultEcoLifePool ?? defaultMainEco;
      case 'spa':
        return imp.spaEcoLife ?? defs.defaultEcoLifeSpa ?? defaultMainEco;
      case 'adu':
        return imp.aduEcoLife ?? defs.defaultEcoLifeADU ?? defaultMainEco;
      case 'manualCost':
        return imp.manualCostEcoLife ?? defs.defaultEcoLifeManualCost ?? defaultMainEco;
      case 'fireplace':
        return imp.fireplaceEcoLife ?? defs.defaultEcoLifeFireplace ?? defaultMainEco;
      default:
        return imp.totalEconomicLife ?? defaultMainEco;
    }
  };

  return Object.entries(byCategory).reduce((sum, [category, price]) => {
    const eco = ecoFor(category);
    const cost = price.toDollarLike();
    return sum + (eco && eco > 0 ? cost / eco : 0);
  }, 0);
}
